import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import RequestAccess from './pages/RequestAccess';
import InviteComplete from './pages/InviteComplete';
import ResetPassword from './pages/ResetPassword';
import MagicLogin from './pages/MagicLogin';
import AdminTenantRequests from './pages/AdminTenantRequests';
import MonthlyTimesheet from './pages/MonthlyTimesheet';
import ActivateService from './pages/ActivateService';
import YearlySummary from './pages/YearlySummary';
import AdminUsersManager from './pages/AdminUsersManager';
import AdminTenantsList from './pages/AdminTenantsList';
import ClientSupplierManager from './pages/ClientSupplierManager';
import ExpenseClaims from './pages/ExpenseClaims';
import ExpenseClaimsApprovals from './pages/ExpenseClaimsApprovals';
import OrderManager from './pages/OrderManager';
import ApprovalsManager from './pages/ApprovalsManager';
import OrderAssignmentManager from './pages/OrderAssignmentManager';
import Dashboard from './pages/Dashboard';
import ApprovalPersonMonth from './pages/ApprovalPersonMonth';
import NationalHolidayManager from './pages/NationalHolidayManager';
import TenantSettings from './pages/TenantSettings';
import LeaveRequests from './pages/LeaveRequests';
import PendingLeaveRequests from './pages/PendingLeaveRequests';
import Documents from './pages/Documents';
import RoleGuard from './guards/RoleGuard';
import { UserRole } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { NationalHolidaysProvider } from './contexts/NationalHolidaysContext';
import { ApprovalCountsProvider } from './contexts/ApprovalCountsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { TenantThemeProvider } from './contexts/TenantThemeProvider';
import { ThemeProvider } from './components/theme-provider';
import { CommandPalette } from './components/CommandPalette';

const App: React.FC = () => {
  const { isAuthReady, isAuthenticated } = useAuth();

  if (!isAuthReady) {
    return null;
  }

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TenantThemeProvider>
        <NotificationProvider>
          <LanguageProvider>
            <div className="min-h-screen bg-background">
            <Routes>
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" />} />
            <Route path="/activate" element={!isAuthenticated ? <ActivateService /> : <Navigate to="/" />} />
            <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/" />} />
            <Route path="/reset-password" element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/" />} />
            <Route path="/register" element={!isAuthenticated ? <RequestAccess /> : <Navigate to="/" />} />
            <Route path="/invite/complete" element={!isAuthenticated ? <InviteComplete /> : <Navigate to="/" />} />
            {/* Monthly reminder email: works whether or not a session is open, it just replaces it */}
            <Route path="/accedi" element={<MagicLogin />} />
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <NationalHolidaysProvider>
                    <ApprovalCountsProvider>
                      <CommandPalette />
                      <Layout />
                    </ApprovalCountsProvider>
                  </NationalHolidaysProvider>
                ) : (
                  <Navigate to="/login" />
                )
              }
            >
              <Route
                index
                element={
                  <RoleGuard allowedRoles={[UserRole.OPERATOR, UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <Dashboard />
                  </RoleGuard>
                }
              />
              <Route
                path="admin/tenant-requests"
                element={
                  <RoleGuard allowedRoles={[UserRole.SUPER_ADMIN]}>
                    <AdminTenantRequests />
                  </RoleGuard>
                }
              />
              <Route
                path="timesheet"
                element={
                  <RoleGuard allowedRoles={[UserRole.OPERATOR, UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <MonthlyTimesheet />
                  </RoleGuard>
                }
              />
              {/* Old "Calendario utenti" bookmarks: other people's months now live under approvals */}
              <Route path="timesheet-reports" element={<Navigate to="/approvals" replace />} />
              <Route
                path="yearly-summary"
                element={
                  <RoleGuard allowedRoles={[UserRole.OPERATOR, UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <YearlySummary />
                  </RoleGuard>
                }
              />
              <Route
                path="leave-requests"
                element={
                  <RoleGuard allowedRoles={[UserRole.OPERATOR, UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <LeaveRequests />
                  </RoleGuard>
                }
              />
              <Route
                path="leave-approvals"
                element={
                  <RoleGuard allowedRoles={[UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <PendingLeaveRequests />
                  </RoleGuard>
                }
              />
              <Route
                path="orders"
                element={
                  <RoleGuard allowedRoles={[UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <OrderManager />
                  </RoleGuard>
                }
              />
              <Route
                path="approvals"
                element={
                  <RoleGuard allowedRoles={[UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <ApprovalsManager />
                  </RoleGuard>
                }
              />
              <Route
                path="approvals/:userId"
                element={
                  <RoleGuard allowedRoles={[UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <ApprovalPersonMonth />
                  </RoleGuard>
                }
              />
              <Route
                path="order-assignments"
                element={
                  <RoleGuard allowedRoles={[UserRole.MANAGER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <OrderAssignmentManager />
                  </RoleGuard>
                }
              />
              <Route
                path="national-holidays"
                element={
                  <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.MANAGER, UserRole.SUPER_ADMIN]}>
                    <NationalHolidayManager />
                  </RoleGuard>
                }
              />
              <Route
                path="client-supplier"
                element={
                  <RoleGuard allowedRoles={[UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <ClientSupplierManager />
                  </RoleGuard>
                }
              />
              <Route
                path="expense-claims"
                element={
                  <RoleGuard allowedRoles={[UserRole.OPERATOR, UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <ExpenseClaims />
                  </RoleGuard>
                }
              />
              <Route
                path="expense-claims-approvals"
                element={
                  <RoleGuard allowedRoles={[UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <ExpenseClaimsApprovals />
                  </RoleGuard>
                }
              />
              <Route
                path="documents"
                element={
                  <RoleGuard allowedRoles={[UserRole.OPERATOR, UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <Documents />
                  </RoleGuard>
                }
              />
              {/* Legacy entry points: old bookmarks, mails and notifications keep working. */}
              <Route path="documents/*" element={<Navigate to="/documents" replace />} />
              <Route path="business-documents/*" element={<Navigate to="/documents" replace />} />
              <Route path="payslips" element={<Navigate to="/documents?tab=BUSTE_PAGA" replace />} />
              <Route
                path="admin/users"
                element={
                  <RoleGuard allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <AdminUsersManager />
                  </RoleGuard>
                }
              />
              <Route
                path="admin/tenants"
                element={
                  <RoleGuard allowedRoles={[UserRole.SUPER_ADMIN]}>
                    <AdminTenantsList />
                  </RoleGuard>
                }
              />
              <Route
                path="settings"
                element={
                  <RoleGuard allowedRoles={[UserRole.OPERATOR, UserRole.MANAGER, UserRole.ACCOUNTANT, UserRole.ADMIN, UserRole.SUPER_ADMIN]}>
                    <TenantSettings />
                  </RoleGuard>
                }
              />
            </Route>
            {/* Catch-all route for undefined paths when authenticated */}
            <Route
              path="*"
              element={isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />}
            />
          </Routes>
            </div>
          </LanguageProvider>
        </NotificationProvider>
      </TenantThemeProvider>
    </ThemeProvider>
  );
};

export default App;
